import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, TrendingUp, Building2, LineChart, ShieldCheck } from "lucide-react";
import { Container, Overline, Reveal, PrimaryLink, GhostLink } from "@/lib/ui";
import { RFPForm } from "@/components/site/RFPForm";
import {
  IMAGES, ASSET_CLASSES, AUDIENCES, SERVICE_PILLARS, WHY, METRICS, LIFECYCLE, PLATFORM, ESG, LEADERSHIP, COMPANY,
} from "@/data/site";
import { HERO, SECTIONS, ASSET, SERVICE } from "@/constants/testIds";

const whyIcons = [Building2, ShieldCheck, LineChart, TrendingUp];

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.05, 1.16]);

  return (
    <section ref={ref} data-testid={HERO.section} className="relative min-h-[92vh] flex items-end overflow-hidden">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img src={IMAGES.hero} alt="Institutional real estate portfolio — modern glass tower" fetchPriority="high" className="w-full h-full object-cover" />
      </motion.div>
      <div className="absolute inset-0 bg-[#0B1A11]/55" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0B1A11] via-transparent to-[#0B1A11]/30" />

      <Container className="relative z-10 pb-16 pt-40 w-full">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.2 }}>
          <Overline dark>Real Estate Asset &amp; Property Management</Overline>
        </motion.div>
        <motion.h1
          initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.35, ease: [0.16, 1, 0.3, 1] }}
          className="mt-7 font-display font-light text-white text-5xl md:text-7xl lg:text-[5.5rem] leading-[0.98] tracking-tight max-w-4xl"
        >
          We maximize property value through operational excellence.
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.6 }}
          className="mt-8 max-w-xl text-white/70 text-lg leading-relaxed"
        >
          GreatLeaf is a vertically integrated platform for asset management, property operations, leasing and
          hospitality — built for institutional owners who measure success in long-term performance.
        </motion.p>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.75 }} className="mt-10 flex flex-wrap gap-4">
          <PrimaryLink to="/contact" testId={HERO.primaryCta} dark>Request a Proposal</PrimaryLink>
          <GhostLink to="/services" testId={HERO.secondaryCta} dark>Explore Our Platform</GhostLink>
        </motion.div>

        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 1 }}
          className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10 border-y border-white/10">
          {METRICS.map((m) => (
            <div key={m.label} className="bg-transparent px-5 py-6">
              <div className="font-data text-3xl md:text-4xl text-white">{m.value}</div>
              <div className="mt-1.5 text-xs text-white/55 leading-snug">{m.label}</div>
            </div>
          ))}
        </motion.div>
      </Container>
    </section>
  );
};

const Audiences = () => (
  <section data-testid={SECTIONS.audiences} className="py-24 md:py-32 bg-[#F7F7F5]">
    <Container>
      <div className="grid lg:grid-cols-12 gap-14 items-center">
        <div className="lg:col-span-5">
          <Overline>Who We Manage For</Overline>
          <h2 className="mt-6 font-display font-light text-4xl md:text-5xl leading-[1.05] tracking-tight text-[#121A16]">
            Trusted by owners who hold real estate for the long term.
          </h2>
          <p className="mt-6 text-[#5C6660] leading-relaxed max-w-md">
            We act as an extension of our clients' investment teams — delivering the reporting, controls and operating
            discipline that institutional capital requires.
          </p>
          <Link to="/about" className="mt-8 inline-flex items-center gap-2 text-sm font-medium text-[#1B3B2B] hover:gap-3 transition-all">
            About the firm <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="lg:col-span-7 grid grid-cols-2 gap-px bg-[#E5E5DF] border border-[#E5E5DF]">
          {AUDIENCES.map((a, i) => (
            <Reveal key={a} delay={i * 0.04}>
              <div className="bg-[#F7F7F5] px-6 py-8 h-full flex items-center">
                <span className="font-data text-xs text-[#275C43] mr-4">{String(i + 1).padStart(2, "0")}</span>
                <span className="text-[15px] font-medium text-[#121A16]">{a}</span>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </Container>
  </section>
);

const AssetClasses = () => (
  <section data-testid={SECTIONS.assetClasses} className="py-24 md:py-32 bg-white border-y border-[#E5E5DF]">
    <Container>
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
        <div>
          <Overline>Asset Classes</Overline>
          <h2 className="mt-6 font-display font-light text-4xl md:text-5xl leading-[1.05] tracking-tight text-[#121A16] max-w-2xl">
            One operating standard across every asset type.
          </h2>
        </div>
        <Link to="/asset-classes" className="inline-flex items-center gap-2 text-sm font-medium text-[#1B3B2B] hover:gap-3 transition-all">
          View all asset classes <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
        {ASSET_CLASSES.map((a, i) => (
          <Reveal key={a.slug} delay={(i % 4) * 0.06}>
            <Link to="/asset-classes" data-testid={ASSET.card(a.slug)} className="group block relative overflow-hidden rounded-sm aspect-[3/4]">
              <img src={a.image} alt={`${a.name} real estate`} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-[800ms] group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0B1A11]/85 via-[#0B1A11]/10 to-transparent" />
              <div className="absolute bottom-0 p-5">
                <h3 className="font-display text-xl text-white">{a.name}</h3>
                <p className="mt-1 text-xs text-white/60 leading-snug line-clamp-2">{a.desc}</p>
              </div>
            </Link>
          </Reveal>
        ))}
      </div>
    </Container>
  </section>
);

const Services = () => (
  <section data-testid={SECTIONS.services} className="py-24 md:py-32 bg-[#F7F7F5]">
    <Container>
      <div className="max-w-2xl mb-14">
        <Overline>Services</Overline>
        <h2 className="mt-6 font-display font-light text-4xl md:text-5xl leading-[1.05] tracking-tight text-[#121A16]">
          A single, accountable platform — from underwriting to disposition.
        </h2>
      </div>
      <div className="grid md:grid-cols-2 gap-5">
        {SERVICE_PILLARS.map((p, i) => (
          <Reveal key={p.slug} delay={(i % 2) * 0.08}>
            <Link to={`/${p.slug === "portfolio-transition" ? "portfolio-transition" : p.slug}`} data-testid={SERVICE.card(p.slug)}
              className="group block bg-white border border-[#E5E5DF] rounded-sm p-8 h-full hover:border-[#1B3B2B] transition-colors duration-300">
              <div className="flex items-start justify-between">
                <h3 className="font-display text-2xl text-[#121A16]">{p.title}</h3>
                <ArrowUpRight className="w-5 h-5 text-[#275C43] opacity-0 group-hover:opacity-100 -translate-x-1 group-hover:translate-x-0 transition-all duration-300" />
              </div>
              <p className="mt-3 text-[#5C6660] leading-relaxed">{p.lead}</p>
              <div className="mt-6 flex flex-wrap gap-2">
                {p.items.map((it) => (
                  <span key={it} className="text-xs font-data text-[#275C43] bg-[#F0F4F1] border border-[#DCE6DF] rounded-sm px-2.5 py-1">{it}</span>
                ))}
              </div>
            </Link>
          </Reveal>
        ))}
      </div>
    </Container>
  </section>
);

const Why = () => (
  <section data-testid={SECTIONS.why} className="py-24 md:py-32 bg-[#1B3B2B] text-white relative grain-dark overflow-hidden">
    <Container className="relative">
      <div className="grid lg:grid-cols-12 gap-14">
        <div className="lg:col-span-4">
          <Overline dark>Why GreatLeaf</Overline>
          <h2 className="mt-6 font-display font-light text-4xl md:text-5xl leading-[1.05] tracking-tight">
            Built to perform, not just to manage.
          </h2>
          <p className="mt-6 text-white/60 leading-relaxed">
            The difference between managing a property and managing an asset is measured in returns. We are engineered for the latter.
          </p>
        </div>
        <div className="lg:col-span-8 grid sm:grid-cols-2 gap-px bg-white/10">
          {WHY.map((w, i) => {
            const Icon = whyIcons[i];
            return (
              <Reveal key={w.title} delay={i * 0.06}>
                <div className="bg-[#1B3B2B] p-8 h-full">
                  <Icon className="w-7 h-7 text-[#8FB8A2] mb-5" strokeWidth={1.3} />
                  <h3 className="font-display text-xl mb-2.5">{w.title}</h3>
                  <p className="text-white/60 text-sm leading-relaxed">{w.desc}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </Container>
  </section>
);

const Portfolio = () => (
  <section data-testid={SECTIONS.portfolio} className="py-24 md:py-32 bg-white border-b border-[#E5E5DF]">
    <Container>
      <div className="max-w-2xl mb-16">
        <Overline>Portfolio Approach</Overline>
        <h2 className="mt-6 font-display font-light text-4xl md:text-5xl leading-[1.05] tracking-tight text-[#121A16]">
          A disciplined lifecycle for every asset we operate.
        </h2>
      </div>
      <div className="relative">
        <div className="hidden lg:block absolute top-[38px] left-0 right-0 h-px bg-[#E5E5DF]" />
        <div className="grid lg:grid-cols-5 gap-8 lg:gap-5">
          {LIFECYCLE.map((s, i) => (
            <Reveal key={s.n} delay={i * 0.08}>
              <div className="relative">
                <div className="flex items-center gap-3 mb-5">
                  <span className="relative z-10 flex items-center justify-center w-[76px] h-[76px] rounded-full bg-[#F7F7F5] border border-[#E5E5DF] font-data text-2xl text-[#1B3B2B]">{s.n}</span>
                </div>
                <h3 className="font-display text-xl text-[#121A16] mb-2">{s.title}</h3>
                <p className="text-sm text-[#5C6660] leading-relaxed">{s.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </Container>
  </section>
);

const Platform = () => (
  <section data-testid={SECTIONS.platform} className="py-24 md:py-32 bg-[#F7F7F5]">
    <Container>
      <div className="grid lg:grid-cols-12 gap-14 items-center">
        <div className="lg:col-span-5">
          <Overline>Operating Platform</Overline>
          <h2 className="mt-6 font-display font-light text-4xl md:text-5xl leading-[1.05] tracking-tight text-[#121A16]">
            Technology and operations, integrated.
          </h2>
          <p className="mt-6 text-[#5C6660] leading-relaxed max-w-md">
            One system connects leasing, revenue, maintenance and reporting — giving owners real-time visibility from a
            single unit to the entire portfolio.
          </p>
          <div className="mt-8 space-y-px bg-[#E5E5DF] border border-[#E5E5DF]">
            {PLATFORM.map((p) => (
              <div key={p.title} className="bg-[#F7F7F5] px-6 py-5">
                <h3 className="font-medium text-[#121A16]">{p.title}</h3>
                <p className="text-sm text-[#5C6660] mt-1">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="lg:col-span-7">
          <Reveal>
            <div className="bg-[#121A16] grain-dark rounded-sm p-6 md:p-8 text-white relative overflow-hidden">
              <div className="relative flex items-center justify-between mb-6">
                <span className="overline text-[#8FB8A2]">Portfolio Performance</span>
                <span className="text-xs font-data text-white/40">Illustrative</span>
              </div>
              <div className="relative grid grid-cols-3 gap-4 mb-6">
                {[["NOI Growth", "+12.4%"], ["Occupancy", "95.8%"], ["Collections", "99.1%"]].map(([l, v]) => (
                  <div key={l} className="border border-white/10 rounded-sm p-4">
                    <div className="text-[11px] text-white/45">{l}</div>
                    <div className="font-data text-2xl mt-1 text-white">{v}</div>
                  </div>
                ))}
              </div>
              <div className="relative border border-white/10 rounded-sm p-5">
                <div className="text-[11px] text-white/45 mb-4">Trailing 12-month NOI index</div>
                <div className="flex items-end gap-2 h-32">
                  {[42, 48, 45, 55, 60, 58, 66, 70, 68, 78, 84, 92].map((h, i) => (
                    <motion.div key={i}
                      initial={{ height: 0 }} whileInView={{ height: `${h}%` }} viewport={{ once: true }}
                      transition={{ duration: 0.7, delay: i * 0.05, ease: [0.16, 1, 0.3, 1] }}
                      className="flex-1 bg-gradient-to-t from-[#275C43] to-[#8FB8A2] rounded-sm" />
                  ))}
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </Container>
  </section>
);

const EsgSection = () => (
  <section data-testid={SECTIONS.esg} className="relative py-24 md:py-32 overflow-hidden">
    <div className="absolute inset-0">
      <img src={IMAGES.esg} alt="Sustainable building with integrated greenery" loading="lazy" className="w-full h-full object-cover" />
      <div className="absolute inset-0 bg-[#0B1A11]/80" />
    </div>
    <Container className="relative text-white">
      <div className="grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <Overline dark>ESG &amp; Sustainability</Overline>
          <h2 className="mt-6 font-display font-light text-4xl md:text-5xl leading-[1.05] tracking-tight">
            Responsible operations that protect long-term value.
          </h2>
          <p className="mt-6 text-white/65 leading-relaxed max-w-md">
            Sustainability is an operating discipline. We reduce consumption, improve resident wellbeing and align
            reporting to the ESG mandates our investors are accountable to.
          </p>
        </div>
        <div className="lg:col-span-7 grid sm:grid-cols-2 gap-px bg-white/10 self-start">
          {ESG.map((e, i) => (
            <Reveal key={e.title} delay={i * 0.06}>
              <div className="bg-[#0B1A11]/40 backdrop-blur-sm p-7 h-full">
                <h3 className="font-display text-xl mb-2">{e.title}</h3>
                <p className="text-sm text-white/60 leading-relaxed">{e.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </Container>
  </section>
);

const LeadershipPreview = () => (
  <section data-testid={SECTIONS.leadership} className="py-24 md:py-32 bg-white border-t border-[#E5E5DF]">
    <Container>
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
        <div>
          <Overline>Leadership</Overline>
          <h2 className="mt-6 font-display font-light text-4xl md:text-5xl leading-[1.05] tracking-tight text-[#121A16] max-w-xl">
            Operators and investors, at the table with you.
          </h2>
        </div>
        <Link to="/leadership" className="inline-flex items-center gap-2 text-sm font-medium text-[#1B3B2B] hover:gap-3 transition-all">
          Meet the team <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {LEADERSHIP.map((l, i) => (
          <Reveal key={l.role} delay={i * 0.06}>
            <div className="bg-[#F7F7F5] border border-[#E5E5DF] rounded-sm p-7 h-full">
              <div className="w-12 h-12 rounded-full bg-[#1B3B2B] text-white font-display text-xl flex items-center justify-center mb-5">
                {l.name.charAt(0)}
              </div>
              <h3 className="font-display text-lg text-[#121A16]">{l.name}</h3>
              <div className="overline text-[#275C43] mt-1 mb-3">{l.role}</div>
              <p className="text-sm text-[#5C6660] leading-relaxed">{l.bio}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </Container>
  </section>
);

const ContactSection = () => (
  <section id="contact" className="py-24 md:py-32 bg-[#F7F7F5] border-t border-[#E5E5DF]">
    <Container>
      <div className="grid lg:grid-cols-12 gap-16">
        <div className="lg:col-span-5">
          <Overline>Contact</Overline>
          <h2 className="mt-6 font-display font-light text-4xl md:text-5xl leading-[1.05] tracking-tight text-[#121A16]">
            Let's discuss your portfolio.
          </h2>
          <p className="mt-6 text-[#5C6660] leading-relaxed max-w-md">
            Whether you are transitioning management, acquiring, or optimizing a held asset, our team will provide a
            tailored operating and performance proposal.
          </p>
        </div>
        <div className="lg:col-span-7">
          <RFPForm />
        </div>
      </div>
    </Container>
  </section>
);

export default function Home() {
  return (
    <>
      <Hero />
      <Audiences />
      <AssetClasses />
      <Services />
      <Why />
      <Portfolio />
      <Platform />
      <EsgSection />
      <LeadershipPreview />
      <ContactSection />
    </>
  );
}
